import { Button } from "@/components/elements/Button";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { useFormField } from "@/components/elements/FormField";
import { COMMON_FILE_TYPES_EXTENSION, COMMON_MINE_TYPES } from "@/constants/file";
import { FCC } from "@/types/common";
import { TExtendFile, TExtendFileOption } from "@/types/file";
import { checkFileListWhetherIsActualPdf, convertByteToMb, getExtensionsOfMimeTypes, getFileStatusWithRules, TFileRules } from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { Stack, styled } from "@mui/material";
import { Key, useCallback, useEffect, useState } from "react";

export type TUploadContactFileBtnProps = {
  nonControl?: boolean;
  readonly?: boolean;
};

export const UploadContactFileBtn: FCC<TUploadContactFileBtnProps> = ({ nonControl = false, readonly }) => {
  const rules: TFileRules = { acceptMimeTypes: COMMON_MINE_TYPES, acceptFileTypesExtension: COMMON_FILE_TYPES_EXTENSION };

  const { field } = useFormField(nonControl);

  const [files, setFiles] = useState<TExtendFile[]>([field?.value ?? []].flat());
  useEffect(() => {
    if (field?.value) {
      const newValue: TExtendFile[] = [field?.value].flat();
      if (files.some((file) => !newValue.find(({ id }) => file.id === id))) {
        setFiles([field?.value].flat());
      }
    } else {
      setFiles([]);
    }
  }, [field]);

  const handleUploadFile = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }

      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }

      if (acceptedFiles.length + files.length > 10) {
        toast.error("ファイルは最大10件までアップロードできます。");
        return;
      }

      if (acceptedFiles.some((item) => convertByteToMb(item.size) > 10)) {
        toast.error(`ファイルサイズは10Mb以内でお願いします。`);
        return;
      }

      if (
        !!rules.acceptMimeTypes &&
        acceptedFiles.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
      ) {
        toast.error(
          `アップロード可能なファイル形式は${getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || [])
            .map((ext) => ext.replace(/^\./, ""))
            .join("、")}です。`,
        );
        return;
      }

      const droppedFiles = acceptedFiles.map((file) => {
        const fileOption: TExtendFileOption = {
          id: makeUuid(),
          status: getFileStatusWithRules(file, rules),
        };

        return Object.assign(file, { ...fileOption });
      });
      const newFiles = [files, droppedFiles].flat();

      setFiles(newFiles);
      triggerChangeFiles(newFiles);
    },
    [files],
  );

  const removeFile = (removedId: Key) => {
    const updatedFiles = files.filter(({ id }) => removedId !== id);
    triggerChangeFiles(updatedFiles);
  };

  const triggerChangeFiles = (files: TExtendFile[]) => {
    field?.onChange(files);
    field?.onBlur();
  };

  return (
    <Wrapper>
      <Button component="label" size="md" variant="outline" fullWidth disabled={readonly}>
        ファイルを選択
        <VisuallyHiddenInput
          type="file"
          accept={rules?.acceptFileTypesExtension?.join(",")}
          onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
          onClick={(event) => {
            event.currentTarget.value = "";
          }}
          multiple
        />
      </Button>
      <Stack flexDirection="column" gap={1} mt={files.length ? 1 : 0}>
        {files.map((file) => (
          <FileItem
            key={file.id}
            filePath={file?.filePath}
            file={file}
            downloadable={true}
            onRemove={!readonly ? () => removeFile(file.id) : undefined}
            maxWidth={336}
          />
        ))}
      </Stack>
    </Wrapper>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Wrapper = styled(Stack)`
  overflow: hidden;
`;
